import { CanonicalContact, ContactRole } from '../helpers/contactShape';
import {
  collectAvailableCategories,
  collectAvailableRoles,
  filterByCategories,
  filterByRoles,
  filterBySearch,
} from '../helpers/contactFilters';
import { groupByRole, GroupedContacts } from '../helpers/groupByRole';
import { useContactPickerData } from './useContactPickerData';
import {
  ContactPickerUIState,
  useContactPickerUIState,
} from './useContactPickerUIState';

interface UseContactPickerOrchestratorArgs {
  serviceRequest: any;
  from: any;
  setFormData: any;
  onSelectedContactChange?: (contact: CanonicalContact | null) => void;
}

export interface ContactPickerOrchestrator {
  isLoading: boolean;

  propertyContacts: CanonicalContact[];
  allContacts: CanonicalContact[];

  groupedResults: GroupedContacts[];
  availableCategories: string[];
  availableRoles: ContactRole[];

  searchTerm: string;
  selectedRoles: ContactRole[];
  selectedCategories: string[];
  selectedContact: CanonicalContact | null;

  onChangeSearchTerm: (term: string) => void;
  onChangeSelectedRoles: (roles: ContactRole[]) => void;
  onChangeSelectedCategories: (categories: string[]) => void;
  onSelectContact: (contact: CanonicalContact) => void;
  onClearSelectedContact: () => void;
}

export const useContactPickerOrchestrator = ({
  serviceRequest,
  from,
  setFormData,
  onSelectedContactChange,
}: UseContactPickerOrchestratorArgs): ContactPickerOrchestrator => {
  const { propertyContacts, allContacts, isLoading } = useContactPickerData({
    serviceRequest,
    from,
  });

  const uiState: ContactPickerUIState = useContactPickerUIState();

  const availableCategories: string[] = collectAvailableCategories(allContacts);
  const availableRoles: ContactRole[] = collectAvailableRoles(allContacts);

  const afterSearch: CanonicalContact[] = filterBySearch(
    allContacts,
    uiState.searchTerm
  );

  const afterRole: CanonicalContact[] = filterByRoles(
    afterSearch,
    uiState.selectedRoles
  );

  const afterCategory: CanonicalContact[] = filterByCategories(
    afterRole,
    uiState.selectedCategories
  );

  const groupedResults: GroupedContacts[] = groupByRole(afterCategory);

  const onSelectContact = (contact: CanonicalContact): void => {
    uiState.onSelectContact(contact);
    setFormData({ userId: contact.id });
    onSelectedContactChange?.(contact);
  };

  const onClearSelectedContact = (): void => {
    uiState.onClearSelectedContact();
    setFormData({ userId: null });
    onSelectedContactChange?.(null);
  };

  return {
    isLoading,
    propertyContacts,
    allContacts,
    groupedResults,
    availableCategories,
    availableRoles,
    searchTerm: uiState.searchTerm,
    selectedRoles: uiState.selectedRoles,
    selectedCategories: uiState.selectedCategories,
    selectedContact: uiState.selectedContact,
    onChangeSearchTerm: uiState.onChangeSearchTerm,
    onChangeSelectedRoles: uiState.onChangeSelectedRoles,
    onChangeSelectedCategories: uiState.onChangeSelectedCategories,
    onSelectContact,
    onClearSelectedContact,
  };
};
